// Raster basemap tiles: source definition, LRU cache and a small fetch-based loader.
//
// Basemap: Esri World Light Gray Base (no labels, very simplified, OSM-based among others).
// Real tiles only exist up to z16; above that the server returns "Map data not yet available"
// placeholders, so the map never requests z > maxNativeZoom and overzooms z16 instead.

export interface TileSource {
  /** URL for tile (z, x, y); x already wrapped into [0, 2^z). */
  url(z: number, x: number, y: number): string;
  /** Highest zoom with real tiles; never request above this. */
  maxNativeZoom: number;
  /** Tile image size in pixels (drawn at 256 CSS px at its own zoom). */
  tileSize: number;
  attribution: string;
  /**
   * Optional custom loader (e.g. client-side vector tile rendering), used instead of the default
   * fetch + createImageBitmap path when present. Must respect the abort signal.
   */
  load?(z: number, x: number, y: number, signal: AbortSignal): Promise<ImageBitmap>;
}

export const ESRI_LIGHT_GRAY_BASE: TileSource = {
  url: (z, x, y) =>
    `https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/${z}/${y}/${x}`,
  maxNativeZoom: 16,
  tileSize: 256,
  attribution: 'Esri, HERE, Garmin, © OpenStreetMap contributors, and the GIS user community',
};

/** Tile zoom used for a (fractional) view zoom: nearest integer, capped at the source's native maximum. */
export function tileZoomFor(viewZoom: number, maxNativeZoom: number): number {
  return Math.max(0, Math.min(maxNativeZoom, Math.round(viewZoom)));
}

export function tileKey(z: number, x: number, y: number): string {
  return `${z}/${x}/${y}`;
}

/** Minimal insertion-ordered LRU. get() refreshes recency; peek() does not. */
export class LruCache<V> {
  private readonly m = new Map<string, V>();

  constructor(
    readonly capacity: number,
    private readonly onEvict?: (key: string, value: V) => void,
  ) {}

  get size(): number {
    return this.m.size;
  }

  get(key: string): V | undefined {
    const v = this.m.get(key);
    if (v !== undefined) {
      this.m.delete(key);
      this.m.set(key, v);
    }
    return v;
  }

  peek(key: string): V | undefined {
    return this.m.get(key);
  }

  set(key: string, value: V): void {
    if (this.m.has(key)) this.m.delete(key);
    this.m.set(key, value);
    while (this.m.size > this.capacity) {
      const oldest = this.m.keys().next().value as string;
      const ov = this.m.get(oldest) as V;
      this.m.delete(oldest);
      this.onEvict?.(oldest, ov);
    }
  }

  delete(key: string): boolean {
    const v = this.m.get(key);
    if (v === undefined) return false;
    this.m.delete(key);
    this.onEvict?.(key, v);
    return true;
  }

  values(): IterableIterator<V> {
    return this.m.values();
  }

  clear(): void {
    for (const [k, v] of this.m) this.onEvict?.(k, v);
    this.m.clear();
  }
}

type TileState = 'queued' | 'loading' | 'ok' | 'error';

export interface Tile {
  key: string;
  z: number;
  x: number;
  y: number;
  state: TileState;
  img: ImageBitmap | null;
  ctrl: AbortController | null;
  /** performance.now() of the last failure (retry after RETRY_MS). */
  failedAt: number;
  /** Frame generation in which the tile was last wanted. */
  wantedGen: number;
  priority: number;
}

const RETRY_MS = 15_000;
const TIMEOUT_MS = 15_000;

export interface TileStats {
  cached: number;
  loaded: number;
  inflight: number;
  requested: number;
  failed: number;
  /** Highest z ever requested from the network (-1 if none). */
  maxRequestedZoom: number;
}

/**
 * Tile loader with LRU cache. Per draw: begin(), want()/get() for each visible tile, flush().
 * Tiles that are in flight but no longer wanted are aborted. Decoding happens off the main thread
 * (fetch + createImageBitmap). onUpdate() fires when a tile finished loading.
 */
export class TileManager {
  private readonly cache: LruCache<Tile>;
  private readonly queue: Tile[] = [];
  private inflight = 0;
  private gen = 0;
  private destroyed = false;
  private readonly st = { requested: 0, loaded: 0, failed: 0, maxRequestedZoom: -1 };

  constructor(
    readonly source: TileSource,
    private readonly onUpdate: () => void,
    capacity = 150,
    private readonly maxInflight = 6,
  ) {
    this.cache = new LruCache<Tile>(capacity, (_k, t) => {
      if (t.state === 'loading') this.inflight--;
      if (t.ctrl) {
        // Clearing ctrl tells load() that the tile was evicted and its slot already released.
        t.ctrl.abort();
        t.ctrl = null;
      }
      t.img?.close();
      t.img = null;
      t.state = 'error';
    });
  }

  /** Loaded tile image (refreshes LRU recency), or null. */
  image(z: number, x: number, y: number): ImageBitmap | null {
    const t = this.cache.get(tileKey(z, x, y));
    return t && t.state === 'ok' ? t.img : null;
  }

  begin(): void {
    this.gen++;
    this.queue.length = 0;
  }

  /** Request a tile for this frame; lower priority value loads first. */
  want(z: number, x: number, y: number, priority: number): void {
    if (z > this.source.maxNativeZoom || z < 0) return;
    const key = tileKey(z, x, y);
    let t = this.cache.get(key);
    if (!t) {
      t = { key, z, x, y, state: 'queued', img: null, ctrl: null, failedAt: 0, wantedGen: 0, priority };
      this.cache.set(key, t);
    }
    t.wantedGen = this.gen;
    t.priority = priority;
    if (t.state === 'error' && performance.now() - t.failedAt > RETRY_MS) t.state = 'queued';
    if (t.state === 'queued') this.queue.push(t);
  }

  flush(): void {
    // Abort stale in-flight loads (view moved on) so the connection slots serve visible tiles.
    for (const t of this.cache.values()) {
      if (t.state === 'loading' && t.wantedGen !== this.gen && t.ctrl) {
        t.ctrl.abort();
      }
    }
    this.queue.sort((a, b) => a.priority - b.priority);
    this.pump();
  }

  private pump(): void {
    while (this.inflight < this.maxInflight && this.queue.length > 0) {
      const t = this.queue.shift() as Tile;
      if (t.state !== 'queued' || t.wantedGen !== this.gen) continue;
      void this.load(t);
    }
  }

  private async load(t: Tile): Promise<void> {
    const ctrl = new AbortController();
    t.ctrl = ctrl;
    t.state = 'loading';
    this.inflight++;
    this.st.requested++;
    if (t.z > this.st.maxRequestedZoom) this.st.maxRequestedZoom = t.z;
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
    let img: ImageBitmap | null = null;
    let aborted = false;
    try {
      if (this.source.load) {
        img = await this.source.load(t.z, t.x, t.y, ctrl.signal);
      } else {
        const res = await fetch(this.source.url(t.z, t.x, t.y), {
          mode: 'cors',
          credentials: 'omit',
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        img = await createImageBitmap(blob);
      }
    } catch {
      aborted = ctrl.signal.aborted;
    } finally {
      clearTimeout(timer);
    }
    if (t.ctrl !== ctrl) {
      // Evicted meanwhile (eviction already fixed the in-flight count).
      img?.close();
      return;
    }
    t.ctrl = null;
    this.inflight--;
    if (this.destroyed) {
      img?.close();
      return;
    }
    if (img) {
      t.img = img;
      t.state = 'ok';
      this.st.loaded++;
      this.onUpdate();
    } else if (aborted && t.wantedGen !== this.gen) {
      t.state = 'queued'; // cancelled because it went out of view; may be wanted again later
    } else {
      t.state = 'error';
      t.failedAt = performance.now();
      this.st.failed++;
    }
    this.pump();
  }

  stats(): TileStats {
    let loaded = 0;
    for (const t of this.cache.values()) if (t.state === 'ok') loaded++;
    return {
      cached: this.cache.size,
      loaded,
      inflight: this.inflight,
      requested: this.st.requested,
      failed: this.st.failed,
      maxRequestedZoom: this.st.maxRequestedZoom,
    };
  }

  destroy(): void {
    this.destroyed = true;
    this.queue.length = 0;
    this.cache.clear();
  }
}
